import { AlertTriangle, Inbox, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { useI18n } from "../../i18n/index.js";
import { Button } from "./button.js";

export function Skeleton({ rows = 4 }: { rows?: number }) {
  const { t } = useI18n();
  return (
    <div aria-busy="true" aria-live="polite" className="space-y-2 py-2">
      <span className="sr-only">{t("common.loading")}</span>
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={index}
          className="h-9 animate-pulse rounded-[var(--radius-card)] bg-surface-inset motion-reduce:animate-none"
        />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon: Icon = Inbox,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-[var(--radius-card)] border border-dashed border-line px-6 py-10 text-center">
      <Icon className="h-8 w-8 text-ink-muted" />
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      <p className="max-w-md text-sm text-ink-muted">{description}</p>
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  message,
  requestId,
  onRetry,
}: {
  message: string;
  requestId?: string;
  onRetry?: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-2 rounded-[var(--radius-card)] border border-action-danger/40 bg-action-danger/5 px-4 py-3"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-action-danger">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        {message}
      </div>
      {requestId ? (
        <p className="text-xs text-ink-muted">
          {t("common.requestId", { id: requestId })}
        </p>
      ) : null}
      {onRetry ? (
        <Button size="sm" variant="secondary" onClick={onRetry}>
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          {t("common.tryAgain")}
        </Button>
      ) : null}
    </div>
  );
}
